import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  OnInit,
  computed,
} from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ShamCreditCardIcon,
  ShamLeadingIcon,
  ShamNocouponIcon,
  ShamMessengerIcon,
  ShamShare08Icon,
  ShamTelegramIcon,
  ShamWhatsappIcon,
  ShamPaymeraLogoIcon,
  ShamCashOnDeliveryIcon,
  ShamEditBoardIcon,
  ShamPlus03Icon,
  ShamSquareArrowReload01Icon,
  ShamClock04Icon,
  ShamCancelCircleIcon,
  ShamAlertDiamondIcon,
  ShamWallet02Icon,
} from 'sham-components/icons';
import {
  CartApi,
  CartItem,
  CheckoutCurrentResponse,
  CheckoutPageSnapshot,
  CheckoutSellerGroup,
} from '../../services/cart-api';
import { QuantitySelector } from '@/app/elements/quantity-selector/quantity-selector';
import { CartItemActions } from '@/app/elements/cart-item-actions/cart-item-actions';
import {
  CustomerCouponsApi,
  CustomerCouponAvailability,
  SellerCouponSelection,
} from '../../services/customer-coupons-api';
import {
  ShopVoucherModal,
  CouponType,
} from '../../elements/shop-voucher-modal/shop-voucher-modal';
import {
  Popup,
  PopupAction,
  PopupButtonsAlign,
  PopupTone,
} from '@/app/elements/popup/popup';
import { OrdersApi, PaymeraRetryPaymentResponseDto } from '@/app/services/orders.api';
import { WebsiteContext } from '@/app/services/website-context';
import { formatWalletAmount } from '@/app/pages/user-profile/wallet/wallet-format';
import { mediaUrl } from '@/app/utils/media-url';

interface CheckoutPaymentPopupState {
  title: string;
  message: string;
  tone: PopupTone;
  primaryButton: PopupAction | null;
  secondaryButton: PopupAction | null;
  buttonsAlign: PopupButtonsAlign;
  closeOnBackdrop: boolean;
  mode:
    | 'redirecting'
    | 'reconciliation-required'
    | 'retry-available'
    | 'order-failed';
}

@Component({
  selector: 'app-checkout-page',
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ShamCreditCardIcon,
    QuantitySelector,
    CartItemActions,
    ShamLeadingIcon,
    ShamNocouponIcon,
    ShamMessengerIcon,
    ShamTelegramIcon,
    ShamWhatsappIcon,
    ShamShare08Icon,
    ShopVoucherModal,
    ShamPaymeraLogoIcon,
    ShamCashOnDeliveryIcon,
    ShamEditBoardIcon,
    ShamPlus03Icon,
    Popup,
    ShamSquareArrowReload01Icon,
    ShamClock04Icon,
    ShamCancelCircleIcon,
    ShamAlertDiamondIcon,
    ShamWallet02Icon,
  ],
  templateUrl: './checkout-page.html',
  styleUrl: './checkout-page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckoutPage implements OnInit {
  private readonly websiteContext = inject(WebsiteContext);
  defaultAddress = computed(() => {
    const addressSelection = this.checkout()?.addressSelection;
    if (!addressSelection) return null;

    return (
      addressSelection.options?.find(
        (address) => address.addressId === addressSelection.selectedAddressId,
      ) ??
      addressSelection.options?.find((address) => address.isDefault) ??
      addressSelection.options?.[0] ??
      null
    );
  });
  translate = inject(TranslateService);
  checkout = signal<CheckoutPageSnapshot | null>(null);
  isLoading = signal(false);
  deliveryErrorKey = signal<string | null>(null);
  showAddressErrorAction = computed(() => {
    const key = this.deliveryErrorKey();
    return (
      key === 'checkout.addressRequired' ||
      key === 'checkout.deliveryLocationNotReady'
    );
  });
  canPlaceOrder = computed(() => this.checkout()?.canPlaceOrder === true);

  readonly mediaUrl = mediaUrl;
  ngOnInit() {
    this.loadCheckout();
    this.loadWishlist();
  }
  favoriteItems = signal<Set<string>>(new Set());

  subtotal = computed(() => this.checkout()?.summary?.subtotal ?? 0);
  shipping = computed(() => this.checkout()?.summary?.shipping ?? 0);
  totalDiscount = computed(() => this.checkout()?.summary?.discountTotal ?? 0);
  total = computed(() => this.checkout()?.summary?.total ?? 0);
  currency = computed(() => this.checkout()?.summary?.currency ?? '');
  paymentPopup = signal<CheckoutPaymentPopupState | null>(null);
  placedOrderId = signal<string | null>(null);
  isRetryingPayment = signal(false);

  groupedItems = computed<CheckoutSellerGroup[]>(
    () => this.checkout()?.sellerGroups ?? [],
  );
  selectedItems = signal<Set<string>>(new Set());
  selectedCount = computed(() =>
    this.groupedItems().reduce((count, seller) => count + seller.items.length, 0),
  );
  paymentMethods = computed(() => {
    const paymentSelection = this.checkout()?.paymentSelection;

    if (!paymentSelection) return [];

    return paymentSelection.options.map((method) => ({
      id: method.code,
      name: method.name,
      description: this.translate.instant(
        method.code === 'CashOnDelivery'
          ? 'Pay cash upon receiving your order'
          : method.code === 'ShamWallet'
            ? 'checkout.wallet.description'
            : 'Pay using Paymera e-Wallet',
      ),
      icon:
        method.code === 'CashOnDelivery'
          ? 'cash'
          : method.code === 'ShamWallet'
            ? 'wallet'
            : 'paymera',
      selected: paymentSelection.selectedPaymentMethodCode === method.code,
      available: method.isAvailable,
      balance:
        method.availableBalance === null
          ? null
          : formatWalletAmount(
              method.availableBalance,
              this.websiteContext.lang() === 'ar' ? 'ar-SY' : 'en-US',
            ),
      balanceCurrency: method.balanceCurrency,
    }));
  });

  walletWarnings = computed(() =>
    (this.checkout()?.warnings ?? []).filter(
      (warning) => warning.code === 'Sham.Checkout.WalletUnavailable',
    ),
  );

  selectPaymentMethod(code: string) {
    const method = this.paymentMethods().find((option) => option.id === code);
    if (!method?.available) return;

    this.isLoading.set(true);
    // PUT /api/v1/checkout/payment-method
    // Payload: { "paymentMethodCode": code }
    this.cartApi.setPaymentMethod(code).subscribe({
      next: (res) => {
        this.isLoading.set(false);
        this.applyCheckoutResponse(res);
      },
      error: (error: unknown) => this.handleCheckoutError(error),
    });
  }

  refreshAddresses() {
    this.loadCheckout();
  }

  // Helper method to synchronize local coupon state from the backend's checkout payload
  private syncCouponsFromCheckout(
    checkoutObj: CheckoutPageSnapshot | null,
  ): { sellerUserId: string; code: string }[] {
    const existingCoupons: { sellerUserId: string; code: string }[] = [];
    if (checkoutObj?.sellerGroups) {
      const loadedCoupons: Record<string, string> = { ...this.selectedCoupons() };

      checkoutObj.sellerGroups.forEach((group) => {
        if (group.appliedCoupon?.code) {
          existingCoupons.push({
            sellerUserId: group.sellerId,
            code: group.appliedCoupon.code,
          });
          loadedCoupons[group.sellerId] = group.appliedCoupon.code;
        }
      });

      this.selectedCoupons.set(loadedCoupons);
    }
    return existingCoupons;
  }

  loadCheckout() {
    this.isLoading.set(true);
    // GET /api/v1/checkout/current
    this.cartApi.getCheckoutCurrent().subscribe({
      next: (res) => {
        this.isLoading.set(false);

        if (res.state === 'StartRequired') {
          this.syncCouponsFromCheckout(res.checkout);

          // POST /api/v1/checkout/start
          this.isLoading.set(true);
          this.cartApi.startCheckout().subscribe({
            next: (startRes) => {
              this.isLoading.set(false);
              this.applyCheckoutResponse(startRes, false);
            },
            error: (error: unknown) => this.handleCheckoutError(error),
          });
          return;
        }

        if (res.state === 'NoSelectedItems') {
          this.applyCheckoutResponse(res);
          return;
        }

        if (res.state === 'Ready') {
          this.applyCheckoutResponse(res);
        }
      },
      error: (error: unknown) => this.handleCheckoutError(error),
    });
  }

  setAddress(addressId: string) {
    this.isLoading.set(true);
    // PUT /api/v1/checkout/shipping-address
    // Payload: { "addressId": "..." }
    this.cartApi.setShippingAddress(addressId).subscribe({
      next: (res) => {
        this.isLoading.set(false);
        this.applyCheckoutResponse(res);
      },
      error: (error: unknown) => this.handleCheckoutError(error),
    });
  }

  editAddress() {
    this.router.navigate(['/shipping-address']);
  }

  placeOrder() {
    const sessionId = this.checkout()?.checkoutSessionId;
    if (!sessionId || !this.canPlaceOrder()) return;

    this.isLoading.set(true);
    // POST /api/v1/orders
    // Payload: { "checkoutSessionId": sessionId }
    this.cartApi.placeOrder(sessionId).subscribe({
      next: (res) => {
        this.placedOrderId.set(res.orderId ?? null);
        if (res.requiresReview) {
          this.isLoading.set(false);
          if (res.reviewRequiredCheckout) {
            this.applyCheckoutResponse(res.reviewRequiredCheckout);
          } else {
            this.checkout.set(null);
          }
          this.deliveryErrorKey.set('checkout.reviewRequired');
          return;
        }
        if (res.payment?.hostedPaymentUrl) {
          this.showRedirectingPopup(res.payment.hostedPaymentUrl);
          this.refreshCartAfterOrder();
          return;
        }

        if (res.payment?.initiationOutcome === 'RetryAvailable') {
          this.showRetryAvailablePopup(res.orderId);
          this.refreshCartAfterOrder();
          return;
        }

        if (res.payment?.initiationOutcome === 'ReconciliationRequired') {
          this.showReconciliationRequiredPopup(res.orderId);
          this.refreshCartAfterOrder();
          return;
        }

        this.refreshCartAfterOrder(res.orderId, true);
      },
      error: (err) => {
        this.isLoading.set(false);
        const errorKey = this.getCheckoutErrorKey(err);
        this.deliveryErrorKey.set(errorKey);
        if (errorKey === 'checkout.reviewRequired') {
          this.checkout.set(null);
          this.loadCheckout();
          return;
        }
        this.showOrderFailedPopup(
          this.extractPaymentErrorMessage(err) ?? 'Server Error',
        );
        console.error(err);
      },
    });
  }

  private cartApi = inject(CartApi);
  private router = inject(Router);
  private customerCouponsApi = inject(CustomerCouponsApi);
  private ordersApi = inject(OrdersApi);

  selectedCoupons = signal<Record<string, string>>({});
  availableCoupons = signal<Record<string, CustomerCouponAvailability[]>>({});
  isVoucherModalOpen = signal<Record<string, boolean>>({});
  isApplyingCoupons = signal(false);
  voucher = '';
  selectAll = signal(true);

  isFavorite(productId: string, variantId: string) {
    return this.favoriteItems().has(`${productId}_${variantId}`);
  }

  loadWishlist() {
    this.cartApi.getWishlist().subscribe({
      next: (wishlist) => {
        const favorites = wishlist.items.map(
          (item) => `${item.productId}_${item.variantId}`,
        );

        this.favoriteItems.set(new Set(favorites));
      },
      error: (err) => {
        console.error('Error loading wishlist:', err);
      },
    });
  }

  toggleFav(item: Pick<CartItem, 'productId' | 'variantId'>) {
    const key = `${item.productId}_${item.variantId}`;

    if (this.favoriteItems().has(key)) {
      this.cartApi.removeFromWishlist(item.productId, item.variantId).subscribe({
        next: () => {
          this.favoriteItems.update((set) => {
            const copy = new Set(set);
            copy.delete(key);
            return copy;
          });
        },
        error: (err) => {
          console.error('Error removing from wishlist:', err);
        },
      });
    } else {
      this.cartApi.addToWishlist(item.productId, item.variantId).subscribe({
        next: () => {
          this.favoriteItems.update((set) => {
            const copy = new Set(set);
            copy.add(key);
            return copy;
          });
        },
        error: (err) => {
          console.error('Error adding to wishlist:', err);
        },
      });
    }
  }

  applyVoucher() {
    console.log('Applying voucher:', this.voucher);
  }

  rebuildAndApplyCoupons() {
    if (this.isApplyingCoupons()) return;

    const selectedCouponsArray = Object.entries(this.selectedCoupons())
      .filter(([_, code]) => code)
      .map(([sellerUserId, code]) => ({ sellerUserId, code }));

    this.isApplyingCoupons.set(true);
    this.cartApi.updateCheckoutCoupons(selectedCouponsArray).subscribe({
      next: (res) => {
        this.isApplyingCoupons.set(false);
        this.applyCheckoutResponse(res);
      },
      error: (err: unknown) => {
        this.isApplyingCoupons.set(false);
        this.deliveryErrorKey.set(this.getCheckoutErrorKey(err));
        console.error('Failed to apply coupons:', err);
      },
    });
  }

  loadAvailableCoupons() {
    const sellerSelections: SellerCouponSelection[] = this.groupedItems().map(
      (seller) => ({
        sellerStoreId: seller.sellerId,
        sellerSubtotal: seller.subtotal,
        currentSelectedCouponId: null,
        currentSelectedCouponCode: this.selectedCoupons()[seller.sellerId] || null,
      }),
    );

    this.customerCouponsApi.listAvailableCoupons({ sellerSelections }).subscribe({
      next: (res) => {
        const couponsBySeller: Record<string, CustomerCouponAvailability[]> = {};
        res.sellers.forEach((seller) => {
          couponsBySeller[seller.sellerStoreId] = seller.coupons;
        });
        this.availableCoupons.set(couponsBySeller);
      },
      error: (err) => {
        console.error('Failed to load available coupons:', err);
      },
    });
  }

  openVoucherModal(sellerId: string) {
    if (Object.keys(this.availableCoupons()).length === 0) {
      this.loadAvailableCoupons();
    }
    this.isVoucherModalOpen.update((state) => ({ ...state, [sellerId]: true }));
  }

  closeVoucherModal(sellerId: string) {
    this.isVoucherModalOpen.update((state) => ({ ...state, [sellerId]: false }));
  }

  onCouponSelected(sellerId: string, coupon: CouponType) {
    this.selectedCoupons.update((state) => ({
      ...state,
      [sellerId]: coupon.code,
    }));
    this.closeVoucherModal(sellerId);
    this.rebuildAndApplyCoupons();
  }

  removeCoupon(sellerId: string) {
    this.selectedCoupons.update((state) => {
      const newState = { ...state };
      delete newState[sellerId];
      return newState;
    });
    this.rebuildAndApplyCoupons();
  }

  getSellerCoupons(sellerId: string): CouponType[] {
    return this.availableCoupons()[sellerId] || [];
  }

  hasAppliedCoupon(sellerId: string): boolean {
    return !!this.selectedCoupons()[sellerId];
  }

  getAppliedCouponCode(sellerId: string): string {
    return this.selectedCoupons()[sellerId] || '';
  }

  getSellerAppliedCoupon(sellerId: string) {
    const seller = this.groupedItems().find((item) => item.sellerId === sellerId);
    return seller?.appliedCoupon || null;
  }

  shareCart() {
    console.log('Sharing cart...');
  }

  closePaymentPopup() {
    if (this.paymentPopup()?.mode === 'redirecting' || this.isRetryingPayment()) {
      return;
    }

    this.paymentPopup.set(null);
  }

  private showRedirectingPopup(hostedPaymentUrl: string) {
    this.paymentPopup.set({
      title: 'Redirecting...',
      message: 'Redirecting to Paymera. Please do not close this page',
      tone: 'default',
      primaryButton: null,
      secondaryButton: null,
      buttonsAlign: 'center',
      closeOnBackdrop: false,
      mode: 'redirecting',
    });

    setTimeout(() => {
      window.location.href = hostedPaymentUrl;
    }, 800);
  }

  private showReconciliationRequiredPopup(orderId: string) {
    this.paymentPopup.set({
      title: 'Verifying Payment',
      message:
        'We are confirming your payment. This might take a few moments. We will update your order status shortly.',
      tone: 'default',
      primaryButton: {
        label: 'View Order',
        link: 'order-details/' + orderId,
      },
      secondaryButton: null,
      buttonsAlign: 'center',
      closeOnBackdrop: true,
      mode: 'reconciliation-required',
    });
  }

  private showRetryAvailablePopup(orderId: string) {
    this.paymentPopup.set({
      title: 'Payment Pending',
      message:
        "Your order has been placed successfully, but the payment process hasn't started yet. Please complete your payment before the time expires.",
      tone: 'default',
      primaryButton: {
        label: 'Try Payment Again',
        width: 'flex',
        closeOnClick: false,
      },
      secondaryButton: {
        label: 'View Order',
        link: ['order-details', orderId],
        width: 'flex',
      },
      buttonsAlign: 'center',
      closeOnBackdrop: true,
      mode: 'retry-available',
    });
  }

  private showOrderFailedPopup(errorMessage: string) {
    this.paymentPopup.set({
      title: 'Order Failed',
      message: `We couldn't process your order due to the following error: ${errorMessage}`,
      tone: 'default',
      primaryButton: {
        label: 'Close',
        width: 'medium',
      },
      secondaryButton: null,
      buttonsAlign: 'center',
      closeOnBackdrop: true,
      mode: 'order-failed',
    });
  }

  private extractPaymentErrorMessage(res: unknown): string | undefined {
    const response = res as {
      error?: { error?: { message?: string }; message?: string };
      payment?: {
        message?: string;
        errorMessage?: string;
        serverErrorMessage?: string;
      };
      message?: string;
    };
    return (
      response?.error?.error?.message ||
      response?.error?.message ||
      response?.payment?.message ||
      response?.payment?.errorMessage ||
      response?.payment?.serverErrorMessage ||
      response?.message ||
      undefined
    );
  }

  private refreshCartAfterOrder(orderId?: string, fallbackToSuccessPage = false) {
    this.cartApi.getCart().subscribe({
      next: () => {
        this.isLoading.set(false);
        if (fallbackToSuccessPage) {
          this.router.navigate(['/order-status'], {
            state: { orderId },
          });
        }
      },
      error: (cartErr) => {
        this.isLoading.set(false);
        console.error('Failed to refresh cart after placing order:', cartErr);
        if (fallbackToSuccessPage) {
          this.router.navigate(['/order-status'], {
            state: { orderId },
          });
        }
      },
    });
  }

  onPaymentPopupPrimaryAction() {
    if (this.paymentPopup()?.mode !== 'retry-available') {
      return;
    }

    const orderId = this.placedOrderId();
    if (!orderId || this.isRetryingPayment()) {
      return;
    }

    this.isRetryingPayment.set(true);
    this.ordersApi.retryPaymeraPayment(orderId).subscribe({
      next: (response: PaymeraRetryPaymentResponseDto) => {
        this.isRetryingPayment.set(false);
        window.location.href = response.hostedPaymentUrl;
      },
      error: () => {
        this.isRetryingPayment.set(false);
      },
    });
  }

  goBack() {
    history.back();
  }

  getOptionsText(options: CartItem['options']): string {
    if (!options || options.length === 0) return '';

    return options
      .map((opt) => `${opt.attributeName}: ${opt.attributeValueName}`)
      .join(', ');
  }

  private applyCheckoutResponse(
    response: CheckoutCurrentResponse,
    restartIfRequired = true,
  ) {
    if (response.state === 'NoSelectedItems') {
      this.checkout.set(null);
      this.router.navigate(['/cart']);
      return;
    }
    if (response.state === 'StartRequired' && restartIfRequired) {
      this.checkout.set(null);
      this.isLoading.set(true);
      this.cartApi.startCheckout().subscribe({
        next: (started) => {
          this.isLoading.set(false);
          this.applyCheckoutResponse(started, false);
        },
        error: (error: unknown) => this.handleCheckoutError(error),
      });
      return;
    }
    if (!response.checkout) {
      this.checkout.set(null);
      const blocker = response.problems.find(
        (problem) => problem.severity === 'Blocker',
      );
      this.deliveryErrorKey.set(
        blocker
          ? (this.checkoutErrorKeyForCode(blocker.code) ??
              'checkout.deliveryQuoteFailed')
          : null,
      );
      return;
    }
    const problems = [...response.problems, ...response.checkout.warnings];
    const knownProblem = problems.find((problem) =>
      this.checkoutErrorKeyForCode(problem.code),
    );
    const unknownBlocker = problems.find((problem) => problem.severity === 'Blocker');
    this.deliveryErrorKey.set(
      knownProblem
        ? this.checkoutErrorKeyForCode(knownProblem.code)
        : unknownBlocker
          ? 'checkout.deliveryQuoteFailed'
          : null,
    );
    this.syncCouponsFromCheckout(response.checkout);
    this.checkout.set(response.checkout);
    this.selectDefaultReadyAddress();
  }

  private selectDefaultReadyAddress() {
    const addressSelection = this.checkout()?.addressSelection;
    if (!addressSelection?.options.length || addressSelection.selectedAddressId)
      return;

    const readyAddress =
      addressSelection.options.find(
        (address) => address.isDefault && address.isDeliveryReady,
      ) ?? addressSelection.options.find((address) => address.isDeliveryReady);
    if (readyAddress) {
      this.setAddress(readyAddress.addressId);
    }
  }

  private handleCheckoutError(error: unknown) {
    this.isLoading.set(false);
    this.deliveryErrorKey.set(this.getCheckoutErrorKey(error));
  }

  private getCheckoutErrorKey(error: unknown): string {
    const response = error as {
      error?: { error?: { code?: string }; code?: string };
    };
    const code = response?.error?.error?.code ?? response?.error?.code;
    return this.checkoutErrorKeyForCode(code) ?? 'checkout.deliveryQuoteFailed';
  }

  private checkoutErrorKeyForCode(code: string | undefined): string | null {
    const errorKeys: Record<string, string> = {
      'Sham.Checkout.ShippingAddressRequired': 'checkout.addressRequired',
      'Sham.Checkout.DeliveryLocationNotReady': 'checkout.deliveryLocationNotReady',
      'Sham.Checkout.DefaultWarehouseRequired': 'checkout.warehouseMissing',
      'Sham.Checkout.PickupLocationNotReady': 'checkout.pickupLocationNotReady',
      'Sham.Checkout.PackageSnapshotNotReady': 'checkout.packageNotReady',
      'Delivery.CustomerQuote.InvalidPackage': 'checkout.packageNotReady',
      'Delivery.Pricing.NotConfigured': 'checkout.pricingNotConfigured',
      'Delivery.CustomerQuote.SettingsUnavailable': 'checkout.pricingNotConfigured',
      'Delivery.LocationIq.UnroutableStops': 'checkout.routeUnavailable',
      'Sham.Checkout.ReviewRequired': 'checkout.reviewRequired',
    };
    return code ? (errorKeys[code] ?? null) : null;
  }
}
